/**
 * Generic request-scoped storage utility for Next.js SSR.
 *
 * Problem:
 * - In SSR, we need isolated instances per request to prevent state leakage
 * - On client, we want a single persistent instance
 *
 * Solution:
 * - Server: Uses AsyncLocalStorage for request-scoped instance isolation
 * - Client: Single instance that persists across navigation
 *
 * Usage:
 * ```ts
 * // Using same factory for both server and client
 * const createMyInstance = () => ({ data: 'example' })
 * const { getInstance, setInstance } = createRequestScopedStorage(createMyInstance)
 *
 * // Using different factories for server and client
 * const createServerInstance = () => ({ data: 'server', env: 'ssr' })
 * const createClientInstance = () => ({ data: 'client', env: 'browser' })
 * const { getInstance, setInstance } = createRequestScopedStorage(
 *   createServerInstance,
 *   createClientInstance
 * )
 *
 * // On server: each request gets its own instance from serverFactory
 * // On client: same instance across all requests from clientFactory (or serverFactory if not provided)
 * const instance = getInstance()
 * ```
 */
import { isOnServer } from '../generalUtils'

export interface RequestScopedStorage<T> {
	/**
	 * Get the current instance. On server, returns request-scoped instance.
	 * On client, returns the persistent instance.
	 */
	getInstance: () => T
	/**
	 * Set a new instance. On server, sets the request-scoped instance.
	 * On client, replaces the persistent instance.
	 */
	setInstance: (instance: T) => void
}

/**
 * Creates a request-scoped storage manager for any type of instance.
 *
 * @param serverFactory - Function that creates a new instance for server-side
 * @param clientFactory - Optional function that creates a new instance for client-side. If not provided, uses serverFactory
 * @returns Object with getInstance and setInstance methods
 */
export function createRequestScopedStorage<T>(
	serverFactory: () => T,
	clientFactory?: () => T,
): RequestScopedStorage<T> {
	let clientInstance: T | undefined

	let serverStorageManager: {
		getInstance: () => T
		setInstance: (instance: T) => void
	}

	if (isOnServer) {
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const { AsyncLocalStorage } = require('node:async_hooks')
		const instanceStorage = new AsyncLocalStorage()

		serverStorageManager = {
			getInstance: () => {
				const instance = instanceStorage.getStore()
				if (!instance) {
					const newInstance = serverFactory()
					instanceStorage.enterWith(newInstance)
					return newInstance
				}
				return instance
			},
			setInstance: (instance) => {
				instanceStorage.enterWith(instance)
			},
		}
	}

	return {
		getInstance: () => {
			if (isOnServer) {
				return serverStorageManager.getInstance()
			}

			if (!clientInstance) {
				const factory = clientFactory || serverFactory
				clientInstance = factory()
			}
			return clientInstance
		},
		setInstance: (instance) => {
			if (isOnServer) {
				serverStorageManager.setInstance(instance)
			} else {
				clientInstance = instance
			}
		},
	}
}
